// Client-side Video Compression
// Uses video + canvas + MediaRecorder to re-encode video files at lower
// bitrate/resolution directly in the browser before uploading.
// Targets: ~720p @ 1.5 Mbps video + 128 kbps audio -> typically 60-80% smaller.

export interface CompressionProgress {
  stage: 'loading' | 'compressing' | 'done';
  percent: number;
}

// Threshold: only compress videos larger than this (8 MB)
const COMPRESS_THRESHOLD = 8 * 1024 * 1024;

// Target max dimension (longest side)
const TARGET_MAX_DIM = 720;

// Target video bitrate
const TARGET_VIDEO_BPS = 1_500_000; // 1.5 Mbps

// Target audio bitrate
const TARGET_AUDIO_BPS = 128_000; // 128 kbps

/**
 * Check if the browser supports canvas-based video compression.
 */
export function canCompressVideo(): boolean {
  if (typeof MediaRecorder === 'undefined') return false;
  return (
    MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ||
    MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ||
    MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ||
    MediaRecorder.isTypeSupported('video/webm')
  );
}

/**
 * Pick the best supported MIME type for recording.
 */
function pickMimeType(): string {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9',
    'video/webm',
  ];
  for (const mt of candidates) {
    if (MediaRecorder.isTypeSupported(mt)) return mt;
  }
  return 'video/webm';
}

/**
 * Returns true if the file should be compressed (large enough + browser capable).
 *
 * DISABLED: Client-side video compression via canvas + MediaRecorder has a
 * fundamental audio reliability problem — it strips audio on many browsers
 * (mobile Safari, some Chrome configs) because createMediaElementSource
 * doesn't reliably capture audio from muted video elements. With upload
 * servers now accepting 500MB+ files, compression is no longer needed.
 */
export function shouldCompressVideo(_file: File): boolean {
  return false;
}

/**
 * Compress a video file client-side.
 * Returns a new, smaller File (webm) or the original if compression isn't beneficial.
 */
export async function compressVideo(
  file: File,
  onProgress?: (p: CompressionProgress) => void,
): Promise<File> {
  if (!canCompressVideo()) return file;

  onProgress?.({ stage: 'loading', percent: 0 });

  // 1. Load the video into a <video> element
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';

  const videoUrl = URL.createObjectURL(file);

  try {
    // Wait for metadata + enough data to start
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Failed to load video for compression'));
      video.src = videoUrl;
    });

    // Wait for enough data to play through
    await new Promise<void>((resolve) => {
      if (video.readyState >= 3) { resolve(); return; }
      video.oncanplaythrough = () => resolve();
      // Fallback timeout -- don't wait forever
      setTimeout(resolve, 5000);
    });

    onProgress?.({ stage: 'loading', percent: 20 });

    const duration = video.duration;
    if (!duration || !isFinite(duration) || duration <= 0) {
      console.warn('[VideoCompress] Cannot determine duration, skipping compression');
      return file;
    }

    // 2. Calculate target dimensions
    const origW = video.videoWidth;
    const origH = video.videoHeight;

    if (!origW || !origH) {
      console.warn('[VideoCompress] Cannot determine video dimensions');
      return file;
    }

    let targetW = origW;
    let targetH = origH;
    const maxDim = Math.max(origW, origH);

    if (maxDim > TARGET_MAX_DIM) {
      const scale = TARGET_MAX_DIM / maxDim;
      targetW = Math.round(origW * scale);
      targetH = Math.round(origH * scale);
      // Ensure even dimensions (required by some codecs)
      targetW = targetW % 2 === 0 ? targetW : targetW + 1;
      targetH = targetH % 2 === 0 ? targetH : targetH + 1;
    }

    // 3. Create canvas for frame rendering
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.warn('[VideoCompress] Canvas 2D context unavailable');
      return file;
    }

    // 4. Set up MediaRecorder to capture the canvas stream + original audio
    const fps = 30;
    const canvasStream = canvas.captureStream(fps);

    // Try to capture audio from the video element.
    // If AudioContext fails (common on mobile), we skip compression entirely
    // rather than producing a silent video — users expect audio to work.
    let combinedStream: MediaStream;
    let audioCtx: AudioContext | null = null;
    try {
      audioCtx = new AudioContext();
      const source = audioCtx.createMediaElementSource(video);
      const dest = audioCtx.createMediaStreamDestination();
      source.connect(dest);
      // Don't connect to audioCtx.destination — avoids audible playback noise

      const audioTracks = dest.stream.getAudioTracks();
      if (audioTracks.length === 0) {
        // Video may have no audio track — that's fine, compress video-only
        console.log('[VideoCompress] No audio tracks detected, compressing video only');
        combinedStream = canvasStream;
      } else {
        combinedStream = new MediaStream([
          ...canvasStream.getVideoTracks(),
          ...audioTracks,
        ]);
      }

      // Mute playback to avoid noise during compression
      video.muted = true;
      video.volume = 0;
    } catch (audioErr) {
      // AudioContext failed — skip compression entirely to preserve audio.
      // This is better than silently stripping the audio track.
      console.warn('[VideoCompress] AudioContext unavailable, skipping compression to preserve audio:', audioErr);
      if (audioCtx) { try { audioCtx.close(); } catch {} }
      return file;
    }

    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(combinedStream, {
      mimeType,
      videoBitsPerSecond: TARGET_VIDEO_BPS,
      audioBitsPerSecond: TARGET_AUDIO_BPS,
    } as any);

    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    // 5. Playback loop: draw each frame onto the canvas
    onProgress?.({ stage: 'compressing', percent: 25 });

    await new Promise<void>((resolve, reject) => {
      let animFrameId: number;
      let stopped = false;

      const drawFrame = () => {
        if (stopped) return;
        ctx.drawImage(video, 0, 0, targetW, targetH);

        // Report progress
        if (duration > 0) {
          const pct = Math.min(95, 25 + Math.round((video.currentTime / duration) * 70));
          onProgress?.({ stage: 'compressing', percent: pct });
        }

        if (!video.ended && !video.paused) {
          animFrameId = requestAnimationFrame(drawFrame);
        }
      };

      recorder.onstop = () => {
        stopped = true;
        cancelAnimationFrame(animFrameId);
        resolve();
      };

      recorder.onerror = (e: any) => {
        stopped = true;
        cancelAnimationFrame(animFrameId);
        reject(new Error('MediaRecorder error: ' + (e.error?.message || 'unknown')));
      };

      video.onended = () => {
        stopped = true;
        cancelAnimationFrame(animFrameId);
        // Give the recorder a moment to flush, then stop
        setTimeout(() => {
          if (recorder.state === 'recording') {
            recorder.stop();
          } else {
            resolve();
          }
        }, 200);
      };

      // Start recording, then play the video
      recorder.start(100); // collect chunks every 100ms
      video.currentTime = 0;
      video.play().then(() => {
        drawFrame();
      }).catch((err) => {
        stopped = true;
        reject(new Error('Video playback failed: ' + err.message));
      });

      // Safety timeout: if video takes too long, stop
      const maxWaitMs = (duration + 10) * 1000;
      setTimeout(() => {
        if (!stopped) {
          stopped = true;
          cancelAnimationFrame(animFrameId);
          if (recorder.state === 'recording') recorder.stop();
        }
      }, maxWaitMs);
    });

    onProgress?.({ stage: 'compressing', percent: 96 });

    // 6. Build the output file
    const blob = new Blob(chunks, { type: mimeType });
    const ext = 'webm';
    const outName = file.name.replace(/\.\w+$/, '') + '_compressed.' + ext;
    const compressed = new File([blob], outName, { type: mimeType });

    // Only use compressed version if it's actually smaller
    if (compressed.size >= file.size * 0.95) {
      console.log(
        `[VideoCompress] Compressed (${(compressed.size / 1024 / 1024).toFixed(1)}MB) not smaller than original (${(file.size / 1024 / 1024).toFixed(1)}MB), using original`
      );
      onProgress?.({ stage: 'done', percent: 100 });
      return file;
    }

    console.log(
      `[VideoCompress] ${(file.size / 1024 / 1024).toFixed(1)}MB -> ${(compressed.size / 1024 / 1024).toFixed(1)}MB (${Math.round((1 - compressed.size / file.size) * 100)}% reduction)`
    );

    onProgress?.({ stage: 'done', percent: 100 });
    return compressed;
  } finally {
    URL.revokeObjectURL(videoUrl);
    video.pause();
    video.src = '';
    video.load();
  }
}

